/**
 * Cookie Service - Manages cookie consent and analytics integration
 */

import { analytics } from '../config/firebase';
import { setAnalyticsCollectionEnabled } from 'firebase/analytics';

// Import analytics service for tracking consent changes
let trackCookieConsent = null;
// Dynamic import to avoid circular dependency
const loadAnalyticsService = async () => {
  if (!trackCookieConsent) {
    try {
      const analyticsModule = await import('./analyticsService');
      trackCookieConsent = analyticsModule.trackCookieConsent;
    } catch (error) {
      console.error('Error loading analytics service:', error);
    }
  }
};

// Check if user has given consent for a specific cookie type
export const hasConsentFor = (cookieType) => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) return false;
    
    const parsedConsent = JSON.parse(consent);
    return parsedConsent[cookieType] === true;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return false;
  }
};

// Get all cookie consent data
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem('cookieConsent');
    return consent ? JSON.parse(consent) : null;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

// Initialize Firebase Analytics only if user has given consent
export const initializeAnalytics = () => {
  if (hasConsentFor('analytics')) {
    try {
      // Enable Firebase Analytics data collection
      setAnalyticsCollectionEnabled(analytics, true);
      console.log('Firebase Analytics enabled - user has given consent');
      
      // If you're also using Google Analytics directly (gtag), uncomment below:
      // const measurementId = 'G-N2ED33PJDR'; // Your measurement ID from firebase config
      // window.gtag('config', measurementId, {
      //   cookie_flags: 'SameSite=None;Secure'
      // });
      
    } catch (error) {
      console.error('Error enabling Firebase Analytics:', error);
    }
  } else {
    // Disable analytics data collection
    try {
      setAnalyticsCollectionEnabled(analytics, false);
      console.log('Firebase Analytics disabled - no user consent');
    } catch (error) {
      console.error('Error disabling Firebase Analytics:', error);
    }
  }
};

// Initialize marketing cookies only if user has given consent
export const initializeMarketing = () => {
  if (hasConsentFor('marketing')) {
    console.log('Marketing cookies initialized - user has given consent');
    
    // Example integrations:
    // Facebook Pixel
    // if (window.fbq) {
    //   window.fbq('consent', 'grant');
    // }
    
    // Google Ads
    // if (window.gtag) {
    //   window.gtag('consent', 'update', {
    //     ad_storage: 'granted',
    //     ad_user_data: 'granted',
    //     ad_personalization: 'granted'
    //   });
    // }
    
  } else {
    console.log('Marketing cookies not initialized - no user consent');
    
    // Revoke marketing consent
    // if (window.fbq) {
    //   window.fbq('consent', 'revoke');
    // }
    
    // if (window.gtag) {
    //   window.gtag('consent', 'update', {
    //     ad_storage: 'denied',
    //     ad_user_data: 'denied',
    //     ad_personalization: 'denied'
    //   });
    // }
  }
};

// Initialize functional cookies only if user has given consent
export const initializeFunctional = () => {
  if (hasConsentFor('functional')) {
    console.log('Functional cookies initialized - user has given consent');
    
    // Example: Chat widgets, video players, etc.
    // Initialize chat widget
    // if (window.Intercom) {
    //   window.Intercom('boot', { app_id: 'your_app_id' });
    // }
    
    // Initialize video players with tracking
    // Enable YouTube/Vimeo enhanced features
    
  } else {
    console.log('Functional cookies not initialized - no user consent');
    
    // Disable functional features
    // if (window.Intercom) {
    //   window.Intercom('shutdown');
    // }
  }
};

// Initialize all services based on user consent
export const initializeConsentBasedServices = () => {
  const consent = getCookieConsent();
  
  if (!consent) {
    console.log('No cookie consent found - only necessary services initialized');
    // Disable analytics by default
    try {
      setAnalyticsCollectionEnabled(analytics, false);
    } catch (error) {
      console.error('Error disabling analytics:', error);
    }
    return;
  }
  
  console.log('Initializing services based on user consent:', consent);
  
  // Always initialize necessary cookies (they're required)
  console.log('Necessary cookies are always active');
  
  // Initialize optional services based on consent
  if (consent.analytics) initializeAnalytics();
  if (consent.marketing) initializeMarketing();
  if (consent.functional) initializeFunctional();
  
  // If analytics is disabled, make sure Firebase Analytics is also disabled
  if (!consent.analytics) {
    try {
      setAnalyticsCollectionEnabled(analytics, false);
    } catch (error) {
      console.error('Error disabling analytics:', error);
    }
  }
};

// Clear all non-necessary cookies
export const clearNonNecessaryCookies = () => {
  console.log('Clearing non-necessary cookies');
  
  // Clear analytics cookies
  try {
    setAnalyticsCollectionEnabled(analytics, false);
  } catch (error) {
    console.error('Error clearing analytics cookies:', error);
  }
  
  // Clear marketing cookies
  const cookiesToClear = [
    '_ga', '_ga_*', '_gid', '_gat', '_gtag_*', // Google Analytics
    '_fbp', '_fbc', // Facebook
    '__hstc', '__hssc', '__hssrc', 'hubspotutk', // HubSpot
    '_gcl_*', // Google Click Identifier
  ];
  
  cookiesToClear.forEach(cookieName => {
    if (cookieName.includes('*')) {
      // Handle wildcard cookies
      const prefix = cookieName.replace('*', '');
      document.cookie.split(';').forEach(cookie => {
        const [name] = cookie.trim().split('=');
        if (name.startsWith(prefix)) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
        }
      });
    } else {
      // Clear specific cookie
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    }
  });
};

// Update services when consent changes
export const updateServicesOnConsentChange = async (newConsent) => {
  console.log('Consent changed, updating services:', newConsent);
  
  // Load analytics service for tracking
  await loadAnalyticsService();
  
  // Track the consent change
  if (trackCookieConsent && newConsent) {
    trackCookieConsent(newConsent);
  }
  
  if (!newConsent) {
    // All consent revoked - disable everything except necessary
    try {
      setAnalyticsCollectionEnabled(analytics, false);
    } catch (error) {
      console.error('Error disabling analytics:', error);
    }
    clearNonNecessaryCookies();
    return;
  }
  
  if (newConsent.analytics) {
    initializeAnalytics();
  } else {
    // Disable analytics if consent was withdrawn
    try {
      setAnalyticsCollectionEnabled(analytics, false);
      console.log('Firebase Analytics disabled - consent withdrawn');
    } catch (error) {
      console.error('Error disabling analytics:', error);
    }
  }
  
  if (newConsent.marketing) {
    initializeMarketing();
  } else {
    // Disable marketing cookies if consent was withdrawn
    console.log('Marketing consent withdrawn');
    // Clear marketing cookies here
  }
  
  if (newConsent.functional) {
    initializeFunctional();
  } else {
    // Disable functional cookies if consent was withdrawn
    console.log('Functional consent withdrawn');
  }
}; 